
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, Users, TrendingUp, UserCheck, UserX, UserMinus } from "lucide-react";
import { StudentWithAttendance } from "@/types/attendance";
import { LateRecord } from "@/hooks/useLateAttendance";
import { memo } from "react";
import { formatCountWithArabicNumbers, formatPercentageWithArabicNumbers, toArabicNumbers } from "@/utils/arabicNumbers";
import { Student } from "@/types/student";

interface LateAttendanceStatsProps {
  studentsWithAttendance: Student[];
  currentLateRecords: LateRecord[];
  lateChanges: {[studentId: string]: 'morning_lineup' | 'after_lineup' | 'period_1' | 'period_2' | 'period_3' | 'period_4' | 'period_5' | 'period_6' | 'period_7' | null};
}

export const LateAttendanceStats = memo(({ 
  studentsWithAttendance, 
  currentLateRecords,
  lateChanges
}: LateAttendanceStatsProps) => {
  const totalStudents = studentsWithAttendance.length;
  
  if (totalStudents === 0) {
    return null;
  }

  // عدد الطلاب المتأخرين (من البيانات المحفوظة + التغييرات المؤقتة)
  const studentsInCurrentClass = studentsWithAttendance.map(s => s.id);
  
  // حساب الطلاب المتأخرين مع الأخذ في الاعتبار التغييرات المؤقتة
  let lateCount = 0;
  studentsInCurrentClass.forEach(studentId => {
    const pendingChange = lateChanges[studentId];
    const existingRecord = currentLateRecords.find(record => record.studentId === studentId);
    
    if (pendingChange !== undefined) {
      // إذا كان هناك تغيير مؤقت
      if (pendingChange !== null) {
        lateCount++;
      }
    } else if (existingRecord) {
      // إذا لم يكن هناك تغيير مؤقت، استخدم البيانات المحفوظة
      lateCount++;
    }
  });

  // إحصائيات مبسطة
  const onTimeCount = totalStudents - lateCount;

  // معدل التأخر
  const lateRate = totalStudents > 0 ? 
    Math.round((lateCount / totalStudents) * 100) : 0;

  // عدد التغييرات المعلقة
  const pendingChangesCount = Object.keys(lateChanges).length;

  return (
    <div className="space-y-4">
      {/* التغييرات المعلقة */}
      {pendingChangesCount > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-orange-700 text-sm font-medium">
            <Clock className="h-4 w-4" />
            تغييرات غير محفوظة: {formatCountWithArabicNumbers(pendingChangesCount)}
          </div>
        </div>
      )}

      {/* البطاقات على سطر واحد */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* إجمالي الطلاب */}
        <div className="relative bg-white rounded-lg p-3 shadow-md border-r-4 border-emerald-500 min-h-[60px]">
          <div className="flex items-center gap-2 w-full">
            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4 text-emerald-600" />
            </div>
            <div className="flex-1 text-center">
              <p className="text-gray-600 text-xs font-medium whitespace-nowrap">إجمالي الطلاب</p>
            </div>
            <div className="flex-shrink-0">
              <p className="text-lg font-bold text-emerald-600">{toArabicNumbers(totalStudents.toString())}</p>
            </div>
          </div>
        </div>

        {/* معدل التأخر */}
        <div className="relative bg-white rounded-lg p-3 shadow-md border-r-4 border-cyan-500 min-h-[60px]">
          <div className="flex items-center gap-2 w-full">
            <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-4 h-4 text-cyan-600" />
            </div>
            <div className="flex-1 text-center">
              <p className="text-gray-600 text-xs font-medium whitespace-nowrap">معدل التأخر</p>
            </div>
            <div className="flex-shrink-0">
              <p className="text-lg font-bold text-cyan-600">{toArabicNumbers(lateRate.toString())}%</p>
            </div>
          </div>
        </div>

        {/* المتأخرون */}
        <div className="relative bg-white rounded-lg p-3 shadow-md border-r-4 border-orange-500 min-h-[60px]">
          <div className="flex items-center gap-2 w-full">
            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              <UserX className="w-4 h-4 text-orange-600" />
            </div>
            <div className="flex-1 text-center">
              <p className="text-gray-600 text-xs font-medium whitespace-nowrap">متأخرون</p>
            </div>
            <div className="flex-shrink-0">
              <p className="text-lg font-bold text-orange-600">{toArabicNumbers(lateCount.toString())}</p>
            </div>
          </div>
        </div>

        {/* غير متأخرين */}
        <div className="relative bg-white rounded-lg p-3 shadow-md border-r-4 border-green-500 min-h-[60px]">
          <div className="flex items-center gap-2 w-full">
            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <UserCheck className="w-4 h-4 text-green-600" />
            </div>
            <div className="flex-1 text-center">
              <p className="text-gray-600 text-xs font-medium whitespace-nowrap">غير متأخرين</p>
            </div>
            <div className="flex-shrink-0">
              <p className="text-lg font-bold text-green-600">{toArabicNumbers(onTimeCount.toString())}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
